import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Spinner } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { useAnalyticsPdf } from './hooks/useAnalyticsPdf'
import { AnalyticsPdfLoadingState, AnalyticsPdfNoAccessState, AnalyticsPdfNoDataState } from './AnalyticsPdfStates'
import { AnalyticsPdfOverviewCards, AnalyticsPdfTimeChart } from './AnalyticsPdfOverview'
import { AnalyticsPdfPageDetailsTable } from './AnalyticsPdfPageDetails'
import { AnalyticsPdfLinkDetailsTable } from './AnalyticsPdfLinkDetails'
import { AnalyticsPdfTabNavigation } from './AnalyticsPdfNavigation'
import styles from './AnalyticsPdf.module.css'
import { updateSite } from 'services/site'
import { fetchUserData } from '../../../Manage/actions'
import { addUpgradeReason } from 'utils/general'

const AnalyticsPdf = ({ domain }) => {
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState('overview')
  const [selectedPeriod, setSelectedPeriod] = useState('30d')
  const [selectedSource, setSelectedSource] = useState('all')

  const {
    pdfAnalytics,
    isLoadingAnalytics,
    overviewStats,
    pageTimeData,
    sources,
    linkAnalytics,
    handleUpgrade,
    hasAccess,
    error
  } = useAnalyticsPdf({ domain, period: selectedPeriod, selectedSource })

  const cleanDomain = useMemo(() => {
    return (domain && domain.subdomain) || domain
  }, [domain])

  const [enabling, setEnabling] = useState(false)
  const [pdfAnalyticsEnabled, setPdfAnalyticsEnabled] = useState(false)
  useEffect(() => {
    let enabled = false
    try {
      enabled = domain?.pdfPageAnalytics
    } catch {}
    setPdfAnalyticsEnabled(enabled)
  }, [domain])

  useEffect(() => {
    if (!isLoadingAnalytics && !hasAccess) addUpgradeReason('View PDF Analytics')
  }, [isLoadingAnalytics, hasAccess])

  const handleEnablePdfAnalytics = async () => {
    setEnabling(true)
    try {
      const formData = new FormData()
      formData.append('siteSettings', JSON.stringify({ pdfPageAnalytics: true }))
      await updateSite(formData, { domain: cleanDomain }, () => {})
      setPdfAnalyticsEnabled(true)
      dispatch(fetchUserData(false))
    } finally {
      setEnabling(false)
    }
  }

  if (isLoadingAnalytics) {
    return <AnalyticsPdfLoadingState />
  }

  if (!hasAccess) {
    return <AnalyticsPdfNoAccessState onUpgrade={handleUpgrade} />
  }

  if (!pdfAnalyticsEnabled) {
    return (
      <div className="text-center p-3 my-3 bg-light rounded">
        <h5 className="mb-2 bold">PDF Page Analytics</h5>
        <p className={`text-muted mb-3 mx-auto ${styles.enablePromptDescription}`}>
          Get detailed insights about visitors to your PDF.
        </p>
        <Button variant="primary" onClick={handleEnablePdfAnalytics} disabled={enabling}>
          {enabling ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Enabling...
            </>
          ) : (
            'Enable PDF Analytics'
          )}
        </Button>
      </div>
    )
  }

  const renderOverviewTab = () => (
    <>
      <AnalyticsPdfOverviewCards stats={overviewStats} />
      {pageTimeData.length > 0 ? <AnalyticsPdfTimeChart pageTimeData={pageTimeData} /> : <AnalyticsPdfNoDataState />}
    </>
  )

  return (
    <div className={styles.pdfAnalyticsWrapper}>
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <AnalyticsPdfTabNavigation
        activeTab={activeTab}
        onTabChange={setActiveTab}
        selectedPeriod={selectedPeriod}
        onPeriodChange={setSelectedPeriod}
        selectedSource={selectedSource}
        sources={sources}
        onSourceChange={setSelectedSource}
      />

      {activeTab === 'overview' && renderOverviewTab()}
      {activeTab === 'pages' && <AnalyticsPdfPageDetailsTable pdfAnalytics={pdfAnalytics} />}
      {activeTab === 'links' && <AnalyticsPdfLinkDetailsTable linkAnalytics={linkAnalytics} />}
    </div>
  )
}

export default AnalyticsPdf
